import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';

interface Feature {
  icon: string;
  title: string;
  description: string;
  color?: string;
  gradientFrom?: string;
  gradientTo?: string;
}

interface FAQ {
  question: string;
  answer: string;
  isOpen: boolean;
}

interface Testimonial {
  quote: string;
  author: string;
  position: string;
  company: string;
  industry: string;
  result: string;
  image?: string;
  logoUrl?: string;
}

interface Industry {
  name: string;
  description: string;
  icon: string;
  iconColor?: string;
  gradientFrom?: string;
  gradientTo?: string;
}

@Component({
  selector: 'app-legislation-monitor',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  template: `
    <!-- Hero Section -->
    <section class="bg-gradient-to-r from-gray-900 to-blue-900 text-white py-20 relative overflow-hidden">
      <div class="absolute inset-0 bg-pattern opacity-10"></div>
      <div class="container mx-auto px-4 relative z-10">
        <div class="flex flex-col md:flex-row items-center">
          <div class="md:w-1/2 mb-10 md:mb-0">
            <div class="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full bg-blue-600/20 backdrop-blur-sm text-sm font-medium mb-6">
              <span class="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></span>
              <span>Regulatory Intelligence</span>
            </div>
            <h1 class="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Legislation Monitor
            </h1>
            <p class="text-xl md:text-2xl mb-8 text-gray-200">
              Stay Ahead of Legislative Changes with accurate, up-to-date information on legislative developments at the federal and state levels.
            </p>
            <div class="flex flex-col sm:flex-row gap-4">
              <a href="/legislation-monitor/demo" class="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300 text-center">
                Start the Demo
              </a>
            </div>
          </div>
          <div class="md:w-1/2">
            <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg">
              <img src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&q=80&w=1000" 
                   alt="Legislation Monitor Dashboard showing regulatory updates and compliance tracking" class="rounded-lg w-full">
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- What Is the Legislation Monitor Section -->
    <section class="py-20 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-4xl md:text-5xl font-extrabold mb-6 text-gray-900 tracking-tight leading-tight">
            What Is the <span class="text-blue-600">Legislation Monitor</span>?
          </h2>
          <div class="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
          <p class="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto font-light leading-relaxed">
            A comprehensive legislative intelligence tool that helps businesses stay ahead of regulatory changes across all industries.
          </p>
        </div>
        
        <div class="max-w-6xl mx-auto">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <!-- Left side: Problem statement -->
            <div class="bg-gray-50 rounded-2xl p-10 shadow-sm border border-gray-200">
              <div class="flex items-center mb-8">
                <div class="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                  <i class="fas fa-exclamation-circle text-gray-500 text-xl"></i>
                </div>
                <h3 class="text-2xl md:text-3xl font-bold text-gray-700">The Challenge</h3>
              </div>
              <p class="text-gray-600 mb-8 text-lg leading-relaxed">
                Legislative changes at federal and state levels significantly impact businesses across all industries. Without proper visibility into these changes, companies face:
              </p>
              <ul class="space-y-6">
                <li class="flex items-start">
                  <span class="text-gray-500 mr-4 mt-1 flex-shrink-0">
                    <i class="fas fa-exclamation-circle text-xl"></i>
                  </span>
                  <div>
                    <span class="font-bold text-gray-700 text-lg">Compliance Risks</span>
                    <p class="text-gray-500 mt-1">Unexpected regulatory changes can put your products and services at risk of non-compliance.</p>
                  </div>
                </li>
                <li class="flex items-start">
                  <span class="text-gray-500 mr-4 mt-1 flex-shrink-0">
                    <i class="fas fa-exclamation-circle text-xl"></i>
                  </span>
                  <div>
                    <span class="font-bold text-gray-700 text-lg">Operational Disruptions</span>
                    <p class="text-gray-500 mt-1">Regulatory shifts can force changes to supply chains, manufacturing processes, and service delivery.</p>
                  </div>
                </li>
                <li class="flex items-start">
                  <span class="text-gray-500 mr-4 mt-1 flex-shrink-0">
                    <i class="fas fa-exclamation-circle text-xl"></i>
                  </span>
                  <div>
                    <span class="font-bold text-gray-700 text-lg">Financial Penalties</span>
                    <p class="text-gray-500 mt-1">Non-compliance can result in fines, legal costs, and remediation expenses.</p>
                  </div>
                </li>
                <li class="flex items-start">
                  <span class="text-gray-500 mr-4 mt-1 flex-shrink-0">
                    <i class="fas fa-exclamation-circle text-xl"></i>
                  </span>
                  <div>
                    <span class="font-bold text-gray-700 text-lg">Reputational Damage</span>
                    <p class="text-gray-500 mt-1">Regulatory issues can erode customer trust and damage brand reputation.</p>
                  </div>
                </li>
              </ul>
            </div>
            
            <!-- Right side: Solution overview -->
            <div class="bg-blue-600 rounded-2xl p-10 shadow-lg border border-blue-500 relative overflow-hidden text-white">
              <div class="absolute top-0 right-0 w-32 h-32 bg-blue-500 rounded-bl-full opacity-30"></div>
              <div class="relative z-10">
                <div class="flex items-center mb-6">
                  <div class="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mr-4">
                    <i class="fas fa-lightbulb text-white text-xl"></i>
                  </div>
                  <h3 class="text-2xl md:text-3xl font-bold text-white">Our Solution</h3>
                </div>
                <p class="text-blue-50 mb-8 text-lg leading-relaxed">
                  The Legislation Monitor provides businesses with timely, accurate, and relevant information on legislative developments, written specifically for business leaders, not lawyers.
                </p>
                <ul class="space-y-6">
                  <li class="flex items-start">
                    <span class="text-white mr-4 mt-1 flex-shrink-0">
                      <i class="fas fa-check-circle text-xl"></i>
                    </span>
                    <div>
                      <span class="font-bold text-white text-lg">Comprehensive Coverage</span>
                      <p class="text-blue-100 mt-1">Track legislative changes across multiple industries and jurisdictions.</p>
                    </div>
                  </li>
                  <li class="flex items-start">
                    <span class="text-white mr-4 mt-1 flex-shrink-0">
                      <i class="fas fa-check-circle text-xl"></i>
                    </span>
                    <div>
                      <span class="font-bold text-white text-lg">Business-Focused Summaries</span>
                      <p class="text-blue-100 mt-1">Clear, jargon-free updates that highlight business implications.</p>
                    </div>
                  </li>
                  <li class="flex items-start">
                    <span class="text-white mr-4 mt-1 flex-shrink-0">
                      <i class="fas fa-check-circle text-xl"></i>
                    </span>
                    <div>
                      <span class="font-bold text-white text-lg">Actionable Intelligence</span>
                      <p class="text-blue-100 mt-1">Insights that help you make informed business decisions with confidence.</p>
                    </div>
                  </li>
                  <li class="flex items-start">
                    <span class="text-white mr-4 mt-1 flex-shrink-0">
                      <i class="fas fa-check-circle text-xl"></i>
                    </span>
                    <div>
                      <span class="font-bold text-white text-lg">Connected Analysis</span>
                      <p class="text-blue-100 mt-1">Legislative updates linked with relevant news, expert analysis, and industry discussions.</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <div class="mt-16 text-center">
            <a href="/legislation-monitor/demo" class="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white text-lg font-bold py-4 px-8 rounded-xl transition duration-300 shadow-md hover:shadow-lg">
              See How It Works
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 ml-2" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Our Solution Section -->
    <section class="py-20 bg-gradient-to-b from-white to-blue-50">
      <div class="container mx-auto px-4">
        <div class="max-w-6xl mx-auto">
          <div class="text-center mb-16">
            <div class="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full bg-blue-100 text-sm font-medium mb-4">
              <span class="w-2 h-2 rounded-full bg-blue-500"></span>
              <span class="text-blue-700">Our Comprehensive Solution</span>
            </div>
            <h2 class="text-4xl md:text-5xl font-bold mt-2 mb-4 text-gray-900">The <span class="text-blue-600">Legislation Monitor</span> Solution</h2>
            <div class="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p class="text-xl text-gray-700 max-w-3xl mx-auto">
              Designed specifically for business leaders, our platform delivers timely, accurate, and relevant legislative information without the legal jargon.
            </p>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <!-- Feature Cards -->
            <div class="bg-white rounded-xl p-8 shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 hover:border-blue-100 hover:translate-y-[-5px]">
              <div class="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mb-6">
                <i class="fas fa-file-alt text-blue-600 text-xl"></i>
              </div>
              <h3 class="text-xl font-bold text-gray-900 mb-3">Comprehensive Coverage</h3>
              <p class="text-gray-600">
                Track legislative changes across multiple industries and jurisdictions with complete visibility into the regulatory landscape.
              </p>
            </div>
            
            <div class="bg-white rounded-xl p-8 shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 hover:border-blue-100 hover:translate-y-[-5px]">
              <div class="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mb-6">
                <i class="fas fa-lightbulb text-blue-600 text-xl"></i>
              </div>
              <h3 class="text-xl font-bold text-gray-900 mb-3">Business-Focused Insights</h3>
              <p class="text-gray-600">
                Clear, jargon-free updates that highlight business implications and actionable recommendations for your organization.
              </p>
            </div>
            
            <div class="bg-white rounded-xl p-8 shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 hover:border-blue-100 hover:translate-y-[-5px]">
              <div class="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mb-6">
                <i class="fas fa-chart-line text-blue-600 text-xl"></i>
              </div>
              <h3 class="text-xl font-bold text-gray-900 mb-3">Competitive Advantage</h3>
              <p class="text-gray-600">
                Stay ahead of regulatory changes and gain a strategic edge by anticipating market shifts before your competitors.
              </p>
            </div>
            
            <div class="bg-white rounded-xl p-8 shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 hover:border-blue-100 hover:translate-y-[-5px]">
              <div class="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mb-6">
                <i class="fas fa-network-wired text-blue-600 text-xl"></i>
              </div>
              <h3 class="text-xl font-bold text-gray-900 mb-3">Connected Analysis</h3>
              <p class="text-gray-600">
                Legislative updates linked with relevant news, expert analysis, and industry discussions for comprehensive context.
              </p>
            </div>
            
            <div class="bg-white rounded-xl p-8 shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 hover:border-blue-100 hover:translate-y-[-5px]">
              <div class="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mb-6">
                <i class="fas fa-clock text-blue-600 text-xl"></i>
              </div>
              <h3 class="text-xl font-bold text-gray-900 mb-3">Time Efficiency</h3>
              <p class="text-gray-600">
                Save countless hours of research with our curated weekly reports and real-time alerts on critical developments.
              </p>
            </div>
            
            <div class="bg-white rounded-xl p-8 shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 hover:border-blue-100 hover:translate-y-[-5px]">
              <div class="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mb-6">
                <i class="fas fa-cogs text-blue-600 text-xl"></i>
              </div>
              <h3 class="text-xl font-bold text-gray-900 mb-3">Customizable Modules</h3>
              <p class="text-gray-600">
                Choose the industry modules you need, from chemicals and packaging to healthcare and financial services.
              </p>
            </div>
          </div>
          
          <div class="mt-16 text-center">
            <a href="/legislation-monitor/demo" class="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white text-lg font-bold py-4 px-8 rounded-xl transition duration-300 shadow-md hover:shadow-lg">
              Explore the Platform
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 ml-2" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Industry Coverage Section -->
    <section class="py-16 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Comprehensive Industry Coverage</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            The Legislation Monitor provides tailored legislative tracking for a wide range of industries, with customizable modules to meet your specific needs.
          </p>
        </div>
        
        <div class="max-w-6xl mx-auto">
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <!-- Industry Cards -->
            <ng-container *ngFor="let industry of industries">
              <div class="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100 hover:border-blue-100">
                <div class="text-blue-600 text-2xl mb-3">
                  <i class="fas {{industry.icon}}"></i>
                </div>
                <h3 class="font-bold text-gray-900 mb-2">{{industry.name}}</h3>
                <p class="text-sm text-gray-600">{{industry.description}}</p>
              </div>
            </ng-container>
            
            <!-- Custom Modules card -->
            <div class="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100 hover:border-blue-100">
              <div class="text-blue-600 text-2xl mb-3">
                <i class="fas fa-plus"></i>
              </div>
              <h3 class="font-bold text-gray-900 mb-2">Custom Modules</h3>
              <p class="text-sm text-gray-600">Tailored legislative tracking for your specific industry or regulatory concerns.</p>
            </div>
          </div>
          
          <div class="mt-12 text-center">
            <p class="text-gray-600 mb-6">Don't see your industry? We offer custom modules tailored to your specific regulatory needs.</p>
            <a href="/legislation-monitor/demo" class="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300">
              Request Custom Coverage
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Success Stories Section -->
    <section class="py-16 bg-gray-50" id="success-stories">
      <div class="container mx-auto px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Success Stories</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            See how businesses across various industries use the Legislation Monitor to stay ahead of regulatory changes and drive strategic advantage.
          </p>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <!-- Success Stories -->
          <ng-container *ngFor="let testimonial of testimonials">
            <div class="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100 flex flex-col h-full hover:shadow-lg transition-all duration-300 hover:border-blue-100">
              <div class="bg-blue-600 text-white p-4">
                <span class="text-sm font-medium uppercase tracking-wider">{{testimonial.industry}}</span>
              </div>
              <div class="p-6 flex-grow">
                <div class="flex items-center mb-4">
                  <div class="flex-shrink-0 h-12 w-12 rounded-full overflow-hidden mr-4 border-2 border-blue-100">
                    <img *ngIf="testimonial.image" [src]="testimonial.image" [alt]="testimonial.author" class="w-full h-full object-cover">
                    <div *ngIf="!testimonial.image" class="bg-blue-50 w-full h-full flex items-center justify-center">
                      <span class="text-blue-600 text-xl font-bold">{{testimonial.author.charAt(0)}}</span>
                    </div>
                  </div>
                  <div>
                    <h3 class="font-bold text-gray-900">{{testimonial.author}}</h3>
                    <p class="text-sm text-gray-600">{{testimonial.position}}, {{testimonial.company}}</p>
                  </div>
                </div>
                <blockquote class="text-gray-700 italic mb-4">
                  "{{testimonial.quote}}"
                </blockquote>
                <div class="bg-blue-50 p-4 rounded-lg border border-blue-100">
                  <p class="text-sm font-semibold text-blue-700">Results:</p>
                  <p class="text-sm text-blue-600 mt-1">{{testimonial.result}}</p>
                </div>
              </div>
            </div>
          </ng-container>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Frequently Asked Questions</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Get answers to common questions about the Legislation Monitor and how it can help your business.
          </p>
        </div>
        
        <div class="max-w-4xl mx-auto">
          <div class="space-y-6">
            <ng-container *ngFor="let faq of faqs; let i = index">
              <div class="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100">
                <button class="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none" 
                        (click)="faq.isOpen = !faq.isOpen">
                  <h3 class="text-lg font-semibold text-gray-900">{{faq.question}}</h3>
                  <span class="text-blue-600">
                    <i class="fas" [ngClass]="faq.isOpen ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                  </span>
                </button>
                <div class="px-6 pb-4" [ngClass]="{'hidden': !faq.isOpen}">
                  <p class="text-gray-700">{{faq.answer}}</p>
                </div>
              </div>
            </ng-container>
          </div>
        </div>
      </div>
    </section>
    
    <!-- CTA Section -->
    <section class="py-16 bg-gray-800 text-white">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto text-center">
          <h2 class="text-3xl md:text-4xl font-bold mb-6">Ready to Stay Ahead of Legislative Changes?</h2>
          <p class="text-xl mb-8 text-gray-200">
            Experience how the Legislation Monitor can transform your approach to regulatory compliance and give you a competitive edge.
          </p>
          
          <div class="text-center">
            <a href="/legislation-monitor/demo" class="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-8 rounded-lg transition duration-300 text-lg shadow-md hover:shadow-lg">
              Start the Demo
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [`
    .bg-pattern {
      background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2V6h4V4H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    }
  `]
})
export class LegislationMonitorComponent implements OnInit {
  features: Feature[] = [
    {
      icon: 'fa-file-alt',
      title: 'Comprehensive Insight',
      description: 'From bills to statutes, the monitor spans all necessary legislative actions, with bill history and sponsor tracking.',
      color: 'blue',
      gradientFrom: 'from-blue-500',
      gradientTo: 'to-blue-600'
    },
    {
      icon: 'fa-laptop',
      title: 'Relevant Summaries',
      description: 'An online, AI-powered dashboard breaks down the significance of each update so you can quickly scan for critical information.',
      color: 'blue',
      gradientFrom: 'from-blue-400',
      gradientTo: 'to-blue-500'
    },
    {
      icon: 'fa-industry',
      title: 'Industry Relevance',
      description: 'Quickly investigate relevant and timely legislative updates using industry-specific categories.',
      color: 'blue',
      gradientFrom: 'from-blue-600',
      gradientTo: 'to-blue-700'
    },
    {
      icon: 'fa-network-wired',
      title: '360-degree Connections',
      description: 'Proprietary technology connects legislative updates with related news, expert analysis, and discussions.',
      color: 'blue',
      gradientFrom: 'from-blue-500',
      gradientTo: 'to-blue-600'
    },
    {
      icon: 'fa-brain',
      title: 'Proactive Intelligence',
      description: 'Stay ahead of legislative changes. Make informed business decisions with confidence.',
      color: 'blue',
      gradientFrom: 'from-blue-400',
      gradientTo: 'to-blue-500'
    },
    {
      icon: 'fa-lightbulb',
      title: 'Market Insight',
      description: 'Choose the modules you need, such as chemicals, packaging, food and beverage, forestry, and the carbon economy.',
      color: 'blue',
      gradientFrom: 'from-blue-500',
      gradientTo: 'to-blue-600'
    }
  ];

  testimonials: Testimonial[] = [
    {
      quote: "The Legislation Monitor has transformed how we handle regulatory updates. The weekly report helps us stay ahead and make confident business decisions.",
      author: "Jennifer Martinez",
      position: "Chief Strategy Officer",
      company: "Global Financial Services",
      industry: "Financial Services",
      result: "15% reduction in compliance costs",
      image: "https://randomuser.me/api/portraits/women/32.jpg"
    },
    {
      quote: "The jargon-free summaries are perfect for our executive team. We can quickly understand new regulations without wading through complex legal text.",
      author: "David Chen",
      position: "Business Development Director",
      company: "Pharma Innovations",
      industry: "Healthcare",
      result: "90% faster regulatory review process",
      image: "https://randomuser.me/api/portraits/men/44.jpg"
    },
    {
      quote: "As a multinational corporation, keeping track of regulations across different jurisdictions was nearly impossible until we started using the Legislation Monitor.",
      author: "Sarah Johnson",
      position: "VP of Corporate Strategy",
      company: "International Manufacturing Group",
      industry: "Manufacturing",
      result: "30% reduction in compliance risks",
      image: "https://randomuser.me/api/portraits/women/68.jpg"
    }
  ];

  industries: Industry[] = [
    {
      name: 'Agriculture',
      description: 'Track regulations on farming practices, pesticides, and agricultural product standards.',
      icon: 'fa-tractor',
      iconColor: 'text-blue-600',
      gradientFrom: 'from-blue-50',
      gradientTo: 'to-blue-100'
    },
    {
      name: 'Building Products',
      description: 'Monitor building codes, material safety requirements, and construction regulations.',
      icon: 'fa-building',
      iconColor: 'text-blue-600',
      gradientFrom: 'from-blue-50',
      gradientTo: 'to-blue-100'
    },
    {
      name: 'Chemicals',
      description: 'Track regulations on chemical manufacturing, labeling, and safety requirements.',
      icon: 'fa-flask',
      iconColor: 'text-blue-600',
      gradientFrom: 'from-blue-50',
      gradientTo: 'to-blue-100'
    },
    {
      name: 'Food & Beverage',
      description: 'Stay updated on food safety, labeling, and ingredient regulations.',
      icon: 'fa-utensils',
      iconColor: 'text-blue-600',
      gradientFrom: 'from-blue-50',
      gradientTo: 'to-blue-100'
    },
    {
      name: 'Forestry & Timberland',
      description: 'Follow legislation on sustainable forestry practices and timber regulations.',
      icon: 'fa-tree',
      iconColor: 'text-blue-600',
      gradientFrom: 'from-blue-50',
      gradientTo: 'to-blue-100'
    },
    {
      name: 'Healthcare',
      description: 'Monitor healthcare policy, patient data regulations, and medical device requirements.',
      icon: 'fa-heartbeat',
      iconColor: 'text-blue-600',
      gradientFrom: 'from-blue-50',
      gradientTo: 'to-blue-100'
    },
    {
      name: 'Household Products',
      description: 'Track safety standards, chemical restrictions, and labeling requirements for household goods.',
      icon: 'fa-home',
      iconColor: 'text-blue-600',
      gradientFrom: 'from-blue-50',
      gradientTo: 'to-blue-100'
    },
    {
      name: 'Metals',
      description: 'Follow regulations on metal production, recycling, and industrial safety standards.',
      icon: 'fa-industry',
      iconColor: 'text-blue-600',
      gradientFrom: 'from-blue-50',
      gradientTo: 'to-blue-100'
    },
    {
      name: 'Packaging',
      description: 'Monitor packaging waste directives, recycling requirements, and material restrictions.',
      icon: 'fa-box',
      iconColor: 'text-blue-600',
      gradientFrom: 'from-blue-50',
      gradientTo: 'to-blue-100'
    },
    {
      name: 'Personal Care',
      description: 'Stay informed on cosmetic regulations, ingredient restrictions, and product testing requirements.',
      icon: 'fa-pump-soap',
      iconColor: 'text-blue-600',
      gradientFrom: 'from-blue-50',
      gradientTo: 'to-blue-100'
    },
    {
      name: 'Printing',
      description: 'Track regulations on inks, solvents, and environmental standards for printing operations.',
      icon: 'fa-print',
      iconColor: 'text-blue-600',
      gradientFrom: 'from-blue-50',
      gradientTo: 'to-blue-100'
    },
    {
      name: 'Pulp & Paper',
      description: 'Monitor environmental regulations, forestry standards, and chemical use in paper production.',
      icon: 'fa-scroll',
      iconColor: 'text-blue-600',
      gradientFrom: 'from-blue-50',
      gradientTo: 'to-blue-100'
    },
    {
      name: 'Retail',
      description: 'Follow consumer protection laws, product safety standards, and e-commerce regulations.',
      icon: 'fa-shopping-cart',
      iconColor: 'text-blue-600',
      gradientFrom: 'from-blue-50',
      gradientTo: 'to-blue-100'
    },
    {
      name: 'Tissue & Hygiene',
      description: 'Stay updated on product safety, testing requirements, and material standards for hygiene products.',
      icon: 'fa-toilet-paper',
      iconColor: 'text-blue-600',
      gradientFrom: 'from-blue-50',
      gradientTo: 'to-blue-100'
    }
  ];

  faqs: FAQ[] = [
    {
      question: 'What benefits does the Legislation Monitor provide?',
      answer: 'The Legislation Monitor provides early awareness of legislative changes, business-focused summaries of complex legal documents, industry-specific insights, and actionable recommendations to help your business stay compliant and gain competitive advantage.',
      isOpen: false
    },
    {
      question: 'Which industries does the Legislation Monitor cover?',
      answer: 'Our platform covers a wide range of industries including Chemicals, Packaging, Food & Beverage, Forestry, Carbon Economy, Healthcare, Financial Services, Manufacturing, Transportation, Energy, Real Estate, and more. We also offer custom modules for specialized industries.',
      isOpen: false
    },
    {
      question: 'How is the information presented?',
      answer: 'Information is delivered through weekly reports, real-time alerts, and an interactive dashboard. All content is written in clear, business-friendly language with highlighted action items and impact assessments specific to your industry.',
      isOpen: false
    },
    {
      question: 'How frequently are updates provided?',
      answer: 'We provide weekly comprehensive reports, plus real-time alerts for critical legislative developments that may impact your business. Our dashboard is updated daily with the latest information.',
      isOpen: false
    },
    {
      question: 'What type of information is included in the legislative updates?',
      answer: 'Updates include summaries of new bills, regulations, and statutes; analysis of potential business impacts; timelines for implementation; compliance requirements; and strategic recommendations for adapting to the changes.',
      isOpen: false
    }
  ];
  
  // Active tab for features section
  activeFeatureTab: string = 'weekly-reports';
  
  constructor(
    private meta: Meta,
    private title: Title
  ) {}
  
  ngOnInit() {
    this.setupSEO();
  }
  
  private setupSEO() {
    this.title.setTitle('Legislation Monitor | Stay Ahead of Regulatory Changes | i2');
    this.meta.addTags([
      { name: 'description', content: 'The Legislation Monitor provides businesses with timely, accurate, and relevant information on legislative developments, written specifically for business leaders, not lawyers.' },
      { name: 'keywords', content: 'legislation monitor, regulatory intelligence, compliance, legislative tracking, regulatory changes, business compliance' },
      { name: 'robots', content: 'index, follow' },
      { property: 'og:title', content: 'Legislation Monitor | Stay Ahead of Regulatory Changes | i2' },
      { property: 'og:description', content: 'The Legislation Monitor provides businesses with timely, accurate, and relevant information on legislative developments, written specifically for business leaders, not lawyers.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://i2.com/products/legislation-monitor' },
      { property: 'og:image', content: 'https://i2.com/assets/images/legislation-monitor-og.jpg' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Legislation Monitor | Stay Ahead of Regulatory Changes | i2' },
      { name: 'twitter:description', content: 'The Legislation Monitor provides businesses with timely, accurate, and relevant information on legislative developments, written specifically for business leaders, not lawyers.' },
      { name: 'twitter:image', content: 'https://i2.com/assets/images/legislation-monitor-twitter.jpg' }
    ]);
  }
  
  // Method to change active feature tab
  setActiveFeatureTab(tab: string): void {
    this.activeFeatureTab = tab;
  }
  
  // Form submission method
  submitDemoRequest(): void {
    // Demo request submission logic would go here
    console.log('Demo request submitted');
    // Redirect to demo page
    window.location.href = '/legislation-monitor/demo';
  }
} 